import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { cn } from '@/lib/utils'
import { IGeoJson2DGeographicCoordinates } from '~/client/graph'
import MapAnchoredCardController from '~/client/src/shared/components/MapAnchoredCard/MapAnchoredCardController'
import {
  measureFirstInputCenter,
  WORKFLOW_PIN_CARD_ANCHOR,
  WORKFLOW_PIN_CARD_SURFACE_CLASS,
} from '~/client/src/shared/components/MapAnchoredCard/workflowPinCardAnchor'
import MapBoxViewerStore from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import { useComposerSheetFrame } from '~/client/src/shared/components/WorkflowComposer/useComposerSheetFrame'
import { WorkflowTypeListSelector } from '~/client/src/shared/components/WorkflowTypeListSelector'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import InitialState from '~/client/src/shared/stores/InitialState'
import { isBaseSurfaceActive } from '~/client/src/shared/utils/keyboardClaim'

type CenterAboveSheet = (
  coordinates: IGeoJson2DGeographicCoordinates,
  sheetTopY: number,
) => void

interface IProps {
  open: boolean
  allowedTypeIds: string[]
  onSelect: (workflowTypeId: string) => void
  onClose: () => void
  mapBoxViewerStore: MapBoxViewerStore
  onCenterAboveSheet: CenterAboveSheet
  state?: InitialState
  isMobileEnv?: boolean
}

const CARD_CLASS = cn(
  'tw:w-80 tw:overflow-hidden tw:text-sm tw:text-popover-foreground',
  WORKFLOW_PIN_CARD_SURFACE_CLASS,
)
// Keeps the pin visible over a map the modal sheet still holds inert.
const CLEAR_OVERLAY_STYLE: React.CSSProperties = {
  backgroundColor: 'transparent',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
}

interface IDesktopProps {
  mapBoxViewerStore: MapBoxViewerStore
  coordinates: IGeoJson2DGeographicCoordinates
  onClose: () => void
  children: React.ReactNode
}

// Placed like the Composer card it hands off to, so the pick doesn't jump.
const DesktopCreateBubble: React.FC<IDesktopProps> = observer(
  ({ mapBoxViewerStore, coordinates, onClose, children }) => {
    const coordinatesRef = React.useRef(coordinates)
    coordinatesRef.current = coordinates
    const [controller] = React.useState(
      () =>
        new MapAnchoredCardController({
          getMap: () => mapBoxViewerStore.mapRef?.getMap(),
          onMapChange: cb => mapBoxViewerStore.onMapChange(cb),
          getCoordinates: () => coordinatesRef.current,
          anchor: WORKFLOW_PIN_CARD_ANCHOR,
          measureAlignOffset: measureFirstInputCenter,
        }),
    )

    React.useLayoutEffect(() => {
      controller.startAnchoring()
      return () => controller.stopAnchoring()
    }, [controller])

    // The card sizes to its list, and the edge clamp only re-runs on camera frames.
    React.useLayoutEffect(() => {
      const wrapper = controller.wrapperRef.current
      if (!wrapper || typeof ResizeObserver === 'undefined') {
        return
      }
      const observer = new ResizeObserver(() => controller.reproject())
      observer.observe(wrapper)
      return () => observer.disconnect()
    }, [controller])

    // A hidden element can't take focus, so wait for the first projection.
    React.useEffect(() => {
      if (controller.hasPosition) {
        controller.wrapperRef.current?.querySelector('input')?.focus()
      }
    }, [controller, controller.hasPosition])

    // The Composer's guards: not from behind an overlay, nor a key already consumed.
    React.useEffect(() => {
      const onKeyDown = (e: KeyboardEvent): void => {
        if (
          e.key === 'Escape' &&
          isBaseSurfaceActive() &&
          !e.defaultPrevented
        ) {
          onClose()
        }
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    // The popover's outside-press dismissal: the markup toolbar sits outside the map
    // container, so onMouseDown never sees a tool press. "Inside" is the React tree, not
    // the DOM: the list's Display popover portals out of the card. The verdict waits for
    // the dispatch to finish, so it never depends on the press bubbling to document.
    const isPressInsideRef = React.useRef(false)
    React.useEffect(() => {
      let verdict: ReturnType<typeof setTimeout>
      const onPointerDown = (): void => {
        isPressInsideRef.current = false
        clearTimeout(verdict)
        verdict = setTimeout(() => {
          if (!isPressInsideRef.current) {
            onClose()
          }
        })
      }
      document.addEventListener('pointerdown', onPointerDown, true)
      return () => {
        clearTimeout(verdict)
        document.removeEventListener('pointerdown', onPointerDown, true)
      }
    }, [onClose])

    return (
      <div
        ref={controller.wrapperRef}
        role="dialog"
        aria-label={Localization.translator.selectWorkflowType}
        className="tw:pointer-events-auto tw:absolute tw:top-0 tw:left-0 tw:z-30"
        style={controller.hasPosition ? undefined : { visibility: 'hidden' }}
        onPointerDownCapture={() => {
          isPressInsideRef.current = true
        }}
      >
        <div className={CARD_CLASS}>{children}</div>
      </div>
    )
  },
)

interface ISheetFrameProps {
  sheetRef: React.RefObject<HTMLElement>
  coordinates: IGeoJson2DGeographicCoordinates
  onCenterAboveSheet: CenterAboveSheet
  isDragging: boolean
}

// Mounted only while open: DrawerContent outlives the close animation, and a framer
// still polling then would clobber the Composer's --markup-toolbar-bottom.
const MobileSheetFrame: React.FC<ISheetFrameProps> = ({
  sheetRef,
  coordinates,
  onCenterAboveSheet,
  isDragging,
}) => {
  const frame = React.useMemo(
    () => ({
      snap: 'expanded' as const,
      centerPinInView: (sheetTopY: number) =>
        onCenterAboveSheet(coordinates, sheetTopY),
    }),
    [coordinates, onCenterAboveSheet],
  )
  useComposerSheetFrame(frame, sheetRef, null, isDragging)
  return null
}

const WorkflowTypeCreateBubble: React.FC<IProps> = observer(
  ({
    open,
    allowedTypeIds,
    onSelect,
    onClose,
    mapBoxViewerStore,
    onCenterAboveSheet,
    state,
    isMobileEnv,
  }) => {
    const sheetRef = React.useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = React.useState(false)
    const preciseLocation = state.preSelectedSiteLocation?.preciseLocation
    const coordinates = React.useMemo<IGeoJson2DGeographicCoordinates>(
      () =>
        preciseLocation && {
          latitude: preciseLocation.latitude,
          longitude: preciseLocation.longitude,
        },
      [preciseLocation],
    )
    const isOpen = open && !!coordinates

    // Sized to content (group headers included) up to the cap, then scrolls.
    const list = (
      <div
        className={cn(
          'tw:flex tw:flex-col',
          isMobileEnv ? 'tw:max-h-99' : 'tw:max-h-98',
        )}
      >
        <WorkflowTypeListSelector
          mode="single"
          title={Localization.translator.selectWorkflowType}
          onClose={onClose}
          onSelect={onSelect}
          allowedTypeIds={allowedTypeIds}
          defaultGroupKey={null}
          parentSizesToContent
        />
      </div>
    )

    if (!isMobileEnv) {
      return isOpen ? (
        <DesktopCreateBubble
          mapBoxViewerStore={mapBoxViewerStore}
          coordinates={coordinates}
          onClose={onClose}
        >
          {list}
        </DesktopCreateBubble>
      ) : null
    }

    // Drag ends on pointer up: vaul's onRelease skips some releases.
    const endDrag = (): void => setIsDragging(false)
    // Toggles `open` rather than unmounting, which would cut vaul's close animation short.
    // noBodyStyles avoids collapsing AutoSizer (STX1-443).
    return (
      <>
        <Drawer
          open={isOpen}
          onOpenChange={next => !next && onClose()}
          noBodyStyles
          onDrag={() => setIsDragging(true)}
        >
          <DrawerContent
            ref={sheetRef}
            aria-describedby={undefined}
            overlayStyle={CLEAR_OVERLAY_STYLE}
            // The clear overlay reads as a live map, so a tap on it must not throw the
            // pick away; swipe-down and Esc still close.
            onPointerDownOutside={e => e.preventDefault()}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <DrawerTitle className="tw:sr-only">
              {Localization.translator.selectWorkflowType}
            </DrawerTitle>
            {list}
          </DrawerContent>
        </Drawer>
        {isOpen && (
          <MobileSheetFrame
            sheetRef={sheetRef}
            coordinates={coordinates}
            onCenterAboveSheet={onCenterAboveSheet}
            isDragging={isDragging}
          />
        )}
      </>
    )
  },
)

export default inject('state', 'isMobileEnv')(WorkflowTypeCreateBubble)
